import { useKitData } from "@/data/use-kits-data";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { useKitUuid } from "@/hooks/use-kit-uuid";
import { useUserLocalSettings } from "@/hooks/use-user-local-settings";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faComment } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useRef, useState } from "react";
import { AssistBody } from "./AssistBody";
import { useAssistContext } from "./AssistContext";

const MIN_WIDTH_PIXELS = 320;
const DEFAULT_WIDTH_PERCENT = 33;
const MIN_WIDTH_PERCENT = 20;
const MAX_WIDTH_PERCENT = 50;

export const AssistContainer: React.FC = () => {
  const kitUuid = useKitUuid();
  const groupUuid = useGroupUuid();
  const { kit } = useKitData(groupUuid, kitUuid);
  const { isExpanded } = useAssistContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const { chatWidth, chatMinimized, setChatMinimized } = useUserLocalSettings();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [transitionWidth, setTransitionWidth] = useState<number | null>(null);

  const handleMinimize = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTransitionWidth(containerRef.current?.offsetWidth || null);
    setChatMinimized(true);
    setTimeout(() => {
      setIsTransitioning(false);
      setTransitionWidth(null);
    }, 500);
  };

  const handleRestore = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTransitionWidth(containerRef.current?.offsetWidth || null);
    setChatMinimized(false);
    setTimeout(() => {
      setIsTransitioning(false);
      setTransitionWidth(null);
    }, 500);
  };

  if (!kit || (kit.type !== "project" && kit.type !== "expert")) return null;

  const widthStyle =
    isExpanded || chatMinimized
      ? "0px"
      : `calc(max(${chatWidth || DEFAULT_WIDTH_PERCENT}%, ${MIN_WIDTH_PERCENT}%, ${MIN_WIDTH_PIXELS}px)`;

  return (
    <>
      <div
        className={cn(
          "transition-[right, transform] absolute right-4 bottom-4 z-40 duration-500",
          {
            "-right-12 rotate-90": isExpanded || !chatMinimized,
            "ease-in": isExpanded || !chatMinimized,
            "ease-out": !isExpanded && chatMinimized,
          },
        )}
      >
        <button
          onClick={() => handleRestore()}
          className={cn(
            "bg-foreground text-background-1 flex h-12 w-12 cursor-pointer items-center justify-center rounded-full p-0",
            "cursor-pointer",
            "transition-[transform, background-color] hover:bg-foreground-8 duration-400 ease-in-out hover:scale-120",
          )}
          aria-label="Open Chat"
          title="Open Chat"
        >
          <FontAwesomeIcon icon={faComment} size="lg" />
        </button>
      </div>

      <div
        className={cn("overflow-hidden", {
          "transition-[width, min-width] duration-500": isTransitioning,
          "absolute top-0 right-0 z-40 h-full w-full": isExpanded,
        })}
        style={{
          width: isExpanded ? "100%" : widthStyle,
          minWidth: isExpanded ? "100%" : widthStyle,
        }}
        ref={containerRef}
      >
        <div
          className={cn(
            "flex h-full w-full flex-col overflow-hidden border-l-1",
          )}
          style={{
            width: transitionWidth ?? undefined,
          }}
        >
          <AssistBody onMinimize={handleMinimize} />
          {!isExpanded && (
            <AssistResizeHandler containerRef={containerRef.current} />
          )}
        </div>
      </div>
    </>
  );
};

const AssistResizeHandler: React.FC<{
  containerRef: HTMLDivElement | null;
}> = ({ containerRef }) => {
  const { setChatWidth } = useUserLocalSettings();
  const newWidthRef = useRef<number | null>(null);
  const [isLimit, setIsLimit] = useState(false);
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();

    const onMouseMove = (e: MouseEvent) => {
      // Disable selection
      e.preventDefault();

      setIsResizing(true);

      const position = e.clientX - 5; // handler offset
      const width = Math.max(window.innerWidth - position, MIN_WIDTH_PIXELS);
      const widthPercent = (100 * width) / window.innerWidth;
      const newWidth = Math.min(
        Math.max(widthPercent, MIN_WIDTH_PERCENT),
        MAX_WIDTH_PERCENT,
      );

      if (containerRef) {
        const oldWidth = containerRef.style.width;
        containerRef.style.width = `${newWidth}%`;
        containerRef.style.minWidth = `${newWidth}%`;

        const rightSidePosition =
          containerRef.offsetLeft + containerRef.offsetWidth;
        if (rightSidePosition > window.innerWidth) {
          containerRef.style.width = oldWidth;
          containerRef.style.minWidth = oldWidth;
          setIsLimit(true);
          return;
        }
      }

      if (
        width <= MIN_WIDTH_PIXELS ||
        widthPercent <= MIN_WIDTH_PERCENT ||
        widthPercent >= MAX_WIDTH_PERCENT
      ) {
        setIsLimit(true);
      } else {
        setIsLimit(false);
        newWidthRef.current = newWidth;
      }
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      setIsResizing(false);
      if (newWidthRef.current !== null) {
        setChatWidth(newWidthRef.current);
      }
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      className={cn(
        "text-foreground-5 l-0 t-0 absolute flex h-full w-4 cursor-col-resize items-center bg-transparent",
        "group/assist-resize-handler",
      )}
    >
      <div
        className={cn(
          "h-full w-[1px]",
          "transition-[background-color, width] duration-200",
          {
            "group-hover/assist-resize-handler:bg-accent-5 group-hover/assist-resize-handler:w-[2px]":
              !isResizing,
            "w-[2px]": isResizing,
            "bg-accent-5": isResizing && !isLimit,
            "bg-red-500": isResizing && isLimit,
          },
        )}
      ></div>
      <div
        className={cn(
          "bg-foreground-5 ml-[4px] h-10 w-[1px]",
          "transition-[background-color] duration-200",
          {
            "group-hover/assist-resize-handler:bg-foreground-10": !isResizing,
            "bg-foreground-10": isResizing && !isLimit,
            "bg-red-500": isResizing && isLimit,
          },
        )}
      />
    </div>
  );
};
